import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Chip,
  Card,
  IconButton,
  Tooltip,
  LinearProgress,
  Divider,
  alpha,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  format,
  isThursday,
  addMonths,
  subMonths,
  isSameDay,
  eachWeekOfInterval,
  addDays
} from 'date-fns';
import { ChevronLeft, ChevronRight, Edit2, Trash2, Plus, ArrowRight, Calendar, List, X } from 'lucide-react';
import axiosInstance from '../utils/axiosConfig';
import { Programming } from '../types/ConcertTypes';
import { DarkDate } from '../types/mongodb';
import PerformanceConfigDialog from './PerformanceConfigDialog';
import { useNavigate } from 'react-router-dom';

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  hasEvent: boolean;
  event?: Programming;
  darkDate?: DarkDate;
}

interface ScheduleViewProps {
  onEdit: (program: Programming) => void;
  onDelete: (program: Programming) => void;
  onAddNew: (date: Date) => void;
  onCreatePerformance: (program: Programming) => void;
initialMonth?: Date | null;  // Add this prop

}

const STATUS_COLORS = {
  BOOKED: '#66bb6a',
  PROVISIONAL: '#ff7700',
  CHECKING: '#42a5f5',
  AVAILABLE: '#ef5350',
  DARK: '#9e9e9e'
} as const;

const CALENDAR_COLORS = {
  BOOKED: alpha('#66bb6a', 0.4),
  PROVISIONAL: alpha('#ff7700', 0.5),
  CHECKING: alpha('#42a5f5', 0.15),
  AVAILABLE: alpha('#ef5350', 0.15),
  DARK: alpha('#9e9e9e', 0.3)
} as const;

const ScheduleView: React.FC<ScheduleViewProps> = ({ 
  onEdit, 
  onDelete, 
  onAddNew, 
  onCreatePerformance,
  initialMonth  // Destructure it here
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [events, setEvents] = useState<Programming[]>([]);
  const [darkDates, setDarkDates] = useState<DarkDate[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Programming | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const navigate = useNavigate();
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedProgramming, setSelectedProgramming] = useState<Programming | null>(null);
  const [showThursdaysOnly, setShowThursdaysOnly] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [hoveredEvent, setHoveredEvent] = useState<{event: Programming, position: {top: number, left: number}} | null>(null);

  useEffect(() => {
  if (initialMonth) {
    setCurrentDate(initialMonth);
  }
}, [initialMonth]);

  useEffect(() => {
    fetchEvents();
  }, [currentDate]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      
      const startDate = startOfMonth(currentDate);
      const endDate = endOfMonth(currentDate);

      const [programmingResponse, darkDatesResponse] = await Promise.all([
        axiosInstance.get('/api/programmings/month', {
          params: {
            month: currentDate.getMonth() + 1,
            year: currentDate.getFullYear(),
          },
        }),
        axiosInstance.get('/api/dark-dates/range', {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString()
          }
        })
      ]);
      
      const eventsWithPerformanceStatus = await Promise.all(
        programmingResponse.data.map(async (event: Programming) => {
          try {
            const perfResponse = await axiosInstance.get(`/api/performances/by-programming/${event._id}`);
            return {
              ...event,
              hasPerformance: !!(perfResponse.data?.data?._id)
            };
          } catch {
            return { ...event, hasPerformance: false };
          }
        })
      );
  
      setEvents(eventsWithPerformanceStatus);
      setDarkDates(darkDatesResponse.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching programming data:', err);
      setError('Unable to load programming data');
    } finally {
      setLoading(false);
    }
  };

  const getThursdaysInMonth = (): Date[] => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    const weeks = eachWeekOfInterval({ start, end });
    return weeks.map(week => {
      let thursday = addDays(week, 4);
      return thursday;
    }).filter(date => isSameMonth(date, currentDate));
  };

  const getAllEventsInMonth = (): { date: Date, event?: Programming, darkDate?: DarkDate }[] => {
    const eventsMap = new Map<string, { date: Date, event?: Programming, darkDate?: DarkDate }>();
    
    // Add all days with events
    events.forEach(event => {
      if (event.date) {
        const eventDate = new Date(event.date);
        if (isSameMonth(eventDate, currentDate)) {
          const dateStr = format(eventDate, 'yyyy-MM-dd');
          eventsMap.set(dateStr, { date: eventDate, event });
        }
      }
    });

    // Add all dark dates
    darkDates.forEach(darkDate => {
      if (darkDate.date) {
        const darkDateObj = new Date(darkDate.date);
        if (isSameMonth(darkDateObj, currentDate)) {
          const dateStr = format(darkDateObj, 'yyyy-MM-dd');
          const existing = eventsMap.get(dateStr);
          if (existing) {
            existing.darkDate = darkDate;
          } else {
            eventsMap.set(dateStr, { date: darkDateObj, darkDate });
          }
        }
      }
    });
    
    // Extract entries to array and sort by date
    return Array.from(eventsMap.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
  };

  const getCalendarDays = (): CalendarDay[][] => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const start = startOfWeek(monthStart);
    const end = endOfWeek(monthEnd);
    
    const days = eachDayOfInterval({ start, end }).map(date => {
      const event = events.find(e => e.date && isSameDay(new Date(e.date), date));
      const darkDate = darkDates.find(d => d.date && isSameDay(new Date(d.date), date));
      return {
        date,
        isCurrentMonth: isSameMonth(date, currentDate),
        hasEvent: !!event || !!darkDate,
        event,
        darkDate
      };
    });

    // Split days into weeks
    const weeks: CalendarDay[][] = [];
    let week: CalendarDay[] = [];

    days.forEach((day) => {
      week.push(day);
      if (week.length === 7) {
        weeks.push(week);
        week = [];
      }
    });

    return weeks;
  };

  // Rest of the component code remains the same until getCalendarColor
  
  const handlePreviousMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const handlePerformanceAction = async (event: Programming) => {
    try {
      const response = await axiosInstance.get(`/api/performances/by-programming/${event._id}`);
      
      if (response.data?.data?._id) {
        navigate(`/admin/performances/${response.data.data._id}`);
      } else {
        setSelectedProgramming(event);
        setConfigDialogOpen(true);
      }
    } catch {
      setSelectedProgramming(event);
      setConfigDialogOpen(true);
    }
  };

  const handleDateClick = (day: CalendarDay) => {
    setSelectedDate(day.date);
    setSelectedEvent(day.event || null);
    
    if (day.darkDate) {
      // Just show info, don't allow editing
      return;
    }
    
    if (day.event) {
      if (day.event.status === 'BOOKED') {
        handlePerformanceAction(day.event);
      } else {
        onEdit(day.event);
      }
    } else {
      onAddNew(day.date);
    }
  };

  const getStatusColor = (status: string | undefined): string => {
    if (!status) return STATUS_COLORS.AVAILABLE;
    return STATUS_COLORS[status as keyof typeof STATUS_COLORS] || STATUS_COLORS.AVAILABLE;
  };

  const getCalendarColor = (status: string | undefined, isDark: boolean = false): string => {
    if (isDark) return CALENDAR_COLORS.DARK;
    if (!status) return 'background.paper';
    return CALENDAR_COLORS[status as keyof typeof CALENDAR_COLORS] || 'background.paper';
  };

  const toggleRowExpansion = (dateKey: string) => {
    setExpandedRows((prev) =>
      prev.includes(dateKey)
        ? prev.filter((c) => c !== dateKey)
        : [...prev, dateKey]
    );
  };

  // Keep all your render functions but update renderCalendarGrid and renderEventCard to show dark dates

  const renderMonthNavigation = () => (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      mb: 2,
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 1
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton onClick={handlePreviousMonth} size={isMobile ? "small" : "medium"}>
          <ChevronLeft />
        </IconButton>
        
        <Typography variant={isMobile ? "h6" : "h5"} sx={{ mx: 2 }}>
          {format(currentDate, 'MMMM yyyy')}
        </Typography>
        
        <IconButton onClick={handleNextMonth} size={isMobile ? "small" : "medium"}>
          <ChevronRight />
        </IconButton>
      </Box>
      
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button 
          variant={viewMode === 'calendar' ? "contained" : "outlined"} 
          size="small"
          startIcon={<Calendar size={16} />}
          onClick={() => setViewMode('calendar')}
        >
          {isMobile ? '' : 'Calendar'}
        </Button>
        
        <Button 
          variant={viewMode === 'list' ? "contained" : "outlined"} 
          size="small"
          startIcon={<List size={16} />}
          onClick={() => setViewMode('list')}
        >
          {isMobile ? '' : 'List'}
        </Button>
      </Box>
    </Box>
  );

  const renderQuickMonthLinks = () => (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        mb: 3,
        overflowX: 'auto',
        gap: 1
      }}
    >
      {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, index) => (
        <Button
          key={month}
          size="small"
          variant={currentDate.getMonth() === index ? "contained" : "text"}
          onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), index, 1))}
          sx={{ 
            minWidth: '40px',
            px: 1,
            color: currentDate.getMonth() === index ? 'white' : 'text.secondary'
          }}
        >
          {month}
        </Button>
      ))}
    </Box>
  );

  const renderCalendarHeader = () => (
    <>
      {!isMobile && renderQuickMonthLinks()}
      {renderMonthNavigation()}
      
      {viewMode === 'calendar' && (
        <Grid container spacing={1} sx={{ mb: 1 }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <Grid item xs key={day}>
              <Typography
                align="center"
                sx={{ 
                  fontWeight: day === 'Thu' ? 'bold' : 'normal', 
                  color: day === 'Thu' ? 'primary.main' : 'text.secondary',
                  fontSize: '0.875rem'
                }}
              >
                {day}
              </Typography>
            </Grid>
          ))}
        </Grid>
      )}
      
      {viewMode === 'list' && (
        <FormControlLabel
          control={
            <Switch
              checked={showThursdaysOnly}
              onChange={(e) => setShowThursdaysOnly(e.target.checked)}
              color="primary"
            />
          }
          label="Thursdays Only"
          sx={{ mb: 2 }}
        />
      )}
    </>
  );

  const renderCalendarGrid = () => (
    <Box>
      {getCalendarDays().map((week, weekIndex) => (
        <Grid container spacing={1} key={weekIndex} sx={{ mb: 1 }}>
          {week.map((day, dayIndex) => {
            const cellHeight = isTablet ? 60 : 80;
            const isDark = !!day.darkDate;
            
            return (
              <Grid item xs key={`${weekIndex}-${dayIndex}`}>
                <Paper
                  sx={{
                    p: 1,
                    height: cellHeight,
                    bgcolor: !day.isCurrentMonth ? alpha('#1e1e1e', 0.5) :
                            isDark ? getCalendarColor(undefined, true) :
                            day.event ? getCalendarColor(day.event.status) : 
                            isThursday(day.date) ? alpha('#42a5f5', 0.05) : 'background.paper',
                    opacity: !day.isCurrentMonth ? 0.5 : 1,
                    cursor: 'pointer',
                    border: selectedDate && isSameDay(day.date, selectedDate) ? 
                           '2px solid #42a5f5' : 
                           isThursday(day.date) ? '1px solid #42a5f5' : 'none',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      transition: 'transform 0.2s'
                    },
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    color: 'text.primary'
                  }}
                  onClick={() => handleDateClick(day)}
                  onMouseEnter={(e) => {
                    if (day.event) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setHoveredEvent({
                        event: day.event,
                        position: { 
                          top: rect.bottom + window.scrollY, 
                          left: rect.left + window.scrollX 
                        }
                      });
                    }
                  }}
                  onMouseLeave={() => setHoveredEvent(null)}
                >
                  <Typography 
                    align="center" 
                    sx={{ 
                      color: isThursday(day.date) ? 'primary.main' : 'inherit',
                      fontWeight: isThursday(day.date) ? 'bold' : 'normal'
                    }}
                  >
                    {format(day.date, 'd')}
                  </Typography>
                  {day.darkDate && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          textAlign: 'center',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: 'text.secondary',
                          fontStyle: 'italic',
                          fontWeight: 500
                        }}
                      >
                        {day.darkDate.reason}
                      </Typography>
                    </Box>
                  )}
                  {day.event && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          textAlign: 'center',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: 'inherit',
                          fontWeight: 500
                        }}
                      >
                        {day.event.artistName}
                      </Typography>
                      {!isTablet && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: 'inherit',
                            fontSize: '0.7rem'
                          }}
                        >
                          {day.event.genre}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      ))}
    </Box>
  );

  const renderEventCard = (dateKey: string, date: Date, event?: Programming, darkDate?: DarkDate) => {
    const isExpanded = expandedRows.includes(dateKey);
    const isEventThursday = isThursday(date);
    const isDark = !!darkDate;
    
    return (
      <Paper
        onClick={() => toggleRowExpansion(dateKey)}
        sx={{
          p: 2,
          bgcolor: isDark ? getCalendarColor(undefined, true) :
                  event ? getCalendarColor(event.status) : 
                  isEventThursday ? alpha('#42a5f5', 0.05) : 'background.paper',
          cursor: 'pointer',
          border: isEventThursday ? '1px solid #42a5f5' : 'none',
          '&:hover': {
            bgcolor: alpha(
              isDark ? getCalendarColor(undefined, true) :
              event ? getCalendarColor(event.status) : 
              isEventThursday ? alpha('#42a5f5', 0.05) : 'background.paper', 
              0.8
            )
          }
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 0
        }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: 'text.primary',
              fontWeight: isEventThursday ? 'bold' : 'normal' 
            }}
          >
            {format(date, 'EEEE, MMMM do')}
            {isEventThursday && (
              <Chip
                label="Thursday"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ ml: 1, fontSize: '0.7rem' }}
              />
            )}
          </Typography>
          
          {darkDate ? (
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: isMobile ? 'space-between' : 'flex-end',
              width: isMobile ? '100%' : 'auto'
            }}>
              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  {darkDate.reason}
                </Typography>
                <Chip
                  label="VENUE CLOSED"
                  size="small"
                  sx={{
                    bgcolor: STATUS_COLORS.DARK,
                    color: 'white'
                  }}
                />
              </Box>
            </Box>
          ) : event ? (
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: isMobile ? 'space-between' : 'flex-end',
              width: isMobile ? '100%' : 'auto'
            }}>
              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ color: 'text.primary' }}>
                  {event.artistName}
                </Typography>
                <Chip
                  label={event.status}
                  size="small"
                  sx={{
                    bgcolor: getStatusColor(event.status),
                    color: event.status === 'PROVISIONAL' ? 'text.primary' : 'white'
                  }}
                />
              </Box>
              
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton 
                  size="small" 
                  onClick={(e) => { e.stopPropagation(); onEdit(event); }}
                  color="primary"
                >
                  <Edit2 size={16} />
                </IconButton>
                <IconButton 
                  size="small" 
                  color="error" 
                  onClick={(e) => { e.stopPropagation(); onDelete(event); }}
                >
                  <Trash2 size={16} />
                </IconButton>
                {event.status === 'BOOKED' && (
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<ArrowRight />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePerformanceAction(event);
                    }}
                    sx={{
                      bgcolor: event.hasPerformance ? alpha('#4CAF50', 0.9) : alpha('#1976D2', 0.9),
                      '&:hover': {
                        bgcolor: event.hasPerformance ? alpha('#4CAF50', 1) : alpha('#1976D2', 1),
                      }
                    }}
                  >
                    {event.hasPerformance ? 'View' : 'Create'}
                  </Button>
                )}
              </Box>
            </Box>
          ) : (
            <Button
              size="small"
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={(e) => { 
                e.stopPropagation(); 
                onAddNew(date);
              }}
            >
              Add Programming
            </Button>
          )}
        </Box>

        {isExpanded && darkDate && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                <Typography variant="body1" color="text.primary">{darkDate.category}</Typography>
              </Grid>
              {darkDate.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                  <Typography variant="body2" color="text.primary">{darkDate.notes}</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {isExpanded && event && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Genre</Typography>
                <Typography variant="body1" color="text.primary">{event.genre || 'Not specified'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Gender</Typography>
                <Typography variant="body1" color="text.primary">{event.ensembleMakeup || 'Not specified'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Primary Contact</Typography>
                <Typography variant="body1" color="text.primary">{event.primaryContact || 'Not specified'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                <Typography variant="body1" color="text.primary">{event.email || 'Not specified'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                <Typography variant="body1" color="text.primary">{event.phone || 'Not specified'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Expected Draw</Typography>
                <Typography variant="body1" color="text.primary">{event.expectedDraw || 'Not specified'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Price</Typography>
                <Typography variant="body1" color="text.primary">
                  {event.pricePoint ? `€${event.pricePoint}` : 'Not specified'}
                </Typography>
              </Grid>
              {event.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                  <Typography variant="body2" color="text.primary">{event.notes}</Typography>
                </Grid>
              )}
              
              {event.status === 'BOOKED' && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={event.hasPerformance ? <ArrowRight /> : <Plus />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePerformanceAction(event);
                      }}
                    >
                      {event.hasPerformance ? 'View Performance Details' : 'Create Performance'}
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Paper>
    );
  };

  const renderEventsView = () => {
    const allEvents = getAllEventsInMonth();
    const thursdays = getThursdaysInMonth();
    
    const displayedEvents = showThursdaysOnly
      ? thursdays.map(thursday => {
          const event = events.find(e => e.date && isSameDay(new Date(e.date), thursday));
          const darkDate = darkDates.find(d => d.date && isSameDay(new Date(d.date), thursday));
          return { date: thursday, event, darkDate };
        })
      : allEvents;
    
    return (
      <Box sx={{ mt: isMobile ? 2 : 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
            {showThursdaysOnly 
              ? `Thursdays in ${format(currentDate, 'MMMM')}` 
              : `Events in ${format(currentDate, 'MMMM')}`}
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {displayedEvents.map(({ date, event, darkDate }) => {
            const dateKey = format(date, 'yyyy-MM-dd');
            return (
              <Grid item xs={12} key={dateKey}>
                {renderEventCard(dateKey, date, event, darkDate)}
              </Grid>
            );
          })}
          
          {displayedEvents.length === 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No events scheduled for {showThursdaysOnly ? 'Thursdays in ' : ''}{format(currentDate, 'MMMM')}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      <Paper sx={{ p: isMobile ? 2 : 3, bgcolor: 'background.paper' }}>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        {renderCalendarHeader()}
        
        {viewMode === 'calendar' && renderCalendarGrid()}
        {viewMode === 'list' && renderEventsView()}
        
{/* Hover Info Card */}
{hoveredEvent && (
  <Box
    sx={{
      position: 'fixed',
      zIndex: 1200,
      top: hoveredEvent.position.top - 120,
      left: hoveredEvent.position.left + 20,
      maxWidth: 300,
      display: isMobile ? 'none' : 'block',
      pointerEvents: 'none'
    }}
  >
    <Card 
      elevation={12}
      sx={{ 
        p: 2.5,
        bgcolor: theme.palette.mode === 'dark' 
          ? alpha('#000000', 0.9)
          : alpha('#ffffff', 0.95),
        borderLeft: `4px solid ${getStatusColor(hoveredEvent.event.status)}`,
        borderRadius: 1,
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
      }}
    >
      <Typography variant="h6" fontWeight="bold">{hoveredEvent.event.artistName}</Typography>
      <Box sx={{ mt: 1.5, mb: 0.5 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
          <Box component="span" sx={{ fontWeight: 'bold', color: 'text.secondary', mr: 1 }}>Status:</Box> 
          <Chip 
            label={hoveredEvent.event.status} 
            size="small" 
            sx={{ 
              bgcolor: getStatusColor(hoveredEvent.event.status),
              color: hoveredEvent.event.status === 'PROVISIONAL' ? 'text.primary' : 'white',
              fontSize: '0.7rem',
              fontWeight: 'bold'
            }}
          />
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <Box component="span" sx={{ fontWeight: 'bold', color: 'text.secondary', mr: 1 }}>Genre:</Box> 
          {hoveredEvent.event.genre || 'Not specified'}
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <Box component="span" sx={{ fontWeight: 'bold', color: 'text.secondary', mr: 1 }}>Price:</Box> 
          {hoveredEvent.event.pricePoint ? `€${hoveredEvent.event.pricePoint}` : 'Not specified'}
        </Typography>
      </Box>
      {hoveredEvent.event.status === 'BOOKED' && (
        <Box sx={{ mt: 1.5, pt: 1, borderTop: 1, borderColor: 'divider' }}>
          {hoveredEvent.event.hasPerformance ? (
            <Chip
              label="Has Performance Details"
              size="small"
              color="success"
              sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}
            />
          ) : (
            <Chip
              label="Needs Performance Details"
              size="small"
              color="warning"
              sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}
            />
          )}
        </Box>
      )}
    </Card>
  </Box>
)}
        
        <Dialog
  open={detailsOpen && isMobile}
  onClose={() => setDetailsOpen(false)}
  fullWidth
  maxWidth="sm"
>
  <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    {selectedDate && format(selectedDate, 'EEEE, MMMM do, yyyy')}
    <IconButton onClick={() => setDetailsOpen(false)}>
      <X />
    </IconButton>
  </DialogTitle>
  <DialogContent>
    {selectedEvent ? (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{selectedEvent.artistName}</Typography>
          <Chip
            label={selectedEvent.status}
            sx={{
              bgcolor: getStatusColor(selectedEvent.status),
              color: selectedEvent.status === 'PROVISIONAL' ? 'text.primary' : 'white'
            }}
          />
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary">Genre</Typography>
            <Typography variant="body1">{selectedEvent.genre || 'Not specified'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary">Price</Typography>
            <Typography variant="body1">
              {selectedEvent.pricePoint ? `€${selectedEvent.pricePoint}` : 'Not specified'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Contact</Typography>
            <Typography variant="body1">{selectedEvent.primaryContact || 'Not specified'}</Typography>
          </Grid>
          
          {selectedEvent.email && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Email</Typography>
              <Typography variant="body1">{selectedEvent.email}</Typography>
            </Grid>
          )}
          
          {selectedEvent.phone && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
              <Typography variant="body1">{selectedEvent.phone}</Typography>
            </Grid>
          )}
          
          {selectedEvent.notes && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
              <Typography variant="body2">{selectedEvent.notes}</Typography>
            </Grid>
          )}
          
          {selectedEvent.status === 'BOOKED' && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={selectedEvent.hasPerformance ? <ArrowRight /> : <Plus />}
                  onClick={() => {
                    setDetailsOpen(false);
                    handlePerformanceAction(selectedEvent);
                  }}
                  fullWidth
                >
                  {selectedEvent.hasPerformance ? 'View Performance Details' : 'Create Performance'}
                </Button>
              </Box>
            </Grid>
          )}
          
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                startIcon={<Edit2 />}
                onClick={() => {
                  setDetailsOpen(false);
                  onEdit(selectedEvent);
                }}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Trash2 />}
                onClick={() => {
                  setDetailsOpen(false);
                  onDelete(selectedEvent);
                }}
              >
                Delete
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    ) : (
      <Box>
        <Typography sx={{ mb: 2 }}>No programming scheduled for this date.</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Plus />}
          fullWidth
          onClick={() => {
            if (selectedDate) {
              setDetailsOpen(false);
              onAddNew(selectedDate);
            }
          }}
        >
          Add Programming
        </Button>
      </Box>
    )}
  </DialogContent>
</Dialog>
            
        <PerformanceConfigDialog
          open={configDialogOpen}
          onClose={() => {
            setConfigDialogOpen(false);
            setSelectedProgramming(null);
          }}
          programming={selectedProgramming}
          onConfirm={async (configData) => {
            try {
              const response = await axiosInstance.post(
                `/api/programmings/${selectedProgramming?._id}/create-performance`,
                configData
              );
              setEvents(prevEvents => 
                prevEvents.map(e => 
                  e._id === selectedProgramming?._id ? { ...e, hasPerformance: true } : e
                )
              );
              setConfigDialogOpen(false);
              setSelectedProgramming(null);
              navigate(`/admin/performances/${response.data._id}`);
            } catch (error) {
              console.error('Error creating performance:', error);
              setError('Failed to create performance');
            }
          }}
        />
      </Paper>
    </Box>
  );
};

export default ScheduleView;